import React, { useRef } from 'react';
import findIndex from 'lodash/findIndex';
import styled from 'styled-components';
import useClickOutside from '~/hooks/useClickOutside';

const SuggestionsWrapper = styled.div`
  position: absolute;
  top: ${(props) => props.inputWidth}px;
  width: 100%;
  background-color: ${(props) => props.theme.color.white500};
  z-index: 20;
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.175);
`;

const SuggestionItem = styled.span`
  margin: 0;
  padding: 13px 15px 13px 70px;
  display: block;
  z-index: 22;
  background-color: ${(props) =>
    props.isActive ? props.theme.color.primary800 : props.theme.color.white500};
  color: ${(props) =>
    props.isActive ? props.theme.color.white500 : props.theme.color.primary800};
  border-top: 1px solid ${(props) => props.theme.color.gray500};

  &:hover {
    color: ${(props) => props.theme.color.primary800};
    background-color: ${(props) => props.theme.color.gray300};
    cursor: pointer;
  }

  @media (${(props) => props.theme.searchHero.mobileSwitch}) {
    padding: 13px 15px;
  }
`;

/**
 * @param {Array<Suggestion>} suggestions
 * @param {React.KeyboardEvent} event
 * @param {(
 *     suggestions: Array<Suggestion> | Array<LocationSuggestion>,
 *   ) => void} setSuggestionsList
 * @param {(suggestion: JobSuggestion | LocationSuggestion) => void} setUserInput
 * @param {(suggestion: JobSuggestion | LocationSuggestion) => void} fireResult
 * @param {string | undefined} [userInput]
 */
export const autoCompleteKeyNav = (
  suggestions,
  event,
  setSuggestionsList,
  setUserInput,
  fireResult,
  userInput,
) => {
  if (suggestions) {
    const cloneSuggestions = [...suggestions];
    const activeIndex = findIndex(
      cloneSuggestions,
      (sugg) => sugg.active === true,
    );

    if (event.key === 'ArrowDown') {
      if (cloneSuggestions[activeIndex]) {
        cloneSuggestions[activeIndex].active = false;
      } else {
        cloneSuggestions[cloneSuggestions.length - 1].active = false;
      }

      if (cloneSuggestions[activeIndex + 1]) {
        cloneSuggestions[activeIndex + 1].active = true;
      } else {
        cloneSuggestions[0].active = true;
      }
      setSuggestionsList(cloneSuggestions);
    }
    if (event.key === 'ArrowUp') {
      if (cloneSuggestions[activeIndex]) {
        cloneSuggestions[activeIndex].active = false;
      } else {
        cloneSuggestions[cloneSuggestions.length - 1].active = false;
      }

      if (cloneSuggestions[activeIndex - 1]) {
        cloneSuggestions[activeIndex - 1].active = true;
      } else {
        cloneSuggestions[cloneSuggestions.length - 1].active = true;
      }
      setSuggestionsList(cloneSuggestions);
    }
    if (event.key === 'Enter') {
      if (cloneSuggestions[activeIndex]) {
        setUserInput(cloneSuggestions[activeIndex]);
        fireResult(cloneSuggestions[activeIndex]);
      } else if (userInput && userInput !== '') {
        /**
         * @type {JobSuggestion}
         */
        const userInputSuggestion = {
          name: userInput,
          active: true,
        };
        fireResult(userInputSuggestion);
      } else if (cloneSuggestions[0]) {
        setUserInput(cloneSuggestions[0]);
        fireResult(cloneSuggestions[0]);
      }
      setSuggestionsList([]);
    }
  }
};

/**
 * @param {AutocompleteFlyoutProps} props
 * @returns {React.ReactElement} The rendered component
 */
const AutocompleteFlyout = ({
  suggestionsList,
  handleSuggestionClick,
  inputWidth = 60,
  className,
  shouldHide,
}) => {
  const wrappeRef = useRef(null);

  useClickOutside(wrappeRef, () => {
    if (shouldHide) {
      shouldHide();
    }
  });

  return (
    <SuggestionsWrapper
      className={className}
      inputWidth={inputWidth}
      ref={wrappeRef}
    >
      {suggestionsList &&
        suggestionsList.map((suggestion) => (
          <SuggestionItem
            key={suggestion.name}
            isActive={suggestion.active}
            onClick={() => handleSuggestionClick(suggestion)}
          >
            {suggestion.name}
          </SuggestionItem>
        ))}
    </SuggestionsWrapper>
  );
};

export default AutocompleteFlyout;
